import { ArrowLeft, Plane, Hotel, Globe2, Headset, ShieldCheck, Clock, BadgeDollarSign } from "lucide-react";
import { FlightQuoteForm } from "./FlightQuoteForm";

const HERO_IMG =
  "https://images.unsplash.com/photo-1603277103691-756354934c2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const PROGRAM = [
  {
    icon: Plane,
    title: "Tickets aéreos",
    desc: "Vuelos nacionales e internacionales al mejor precio. Solo ida, ida y vuelta o multidestino: nosotros buscamos las mejores tarifas y conexiones.",
  },
  {
    icon: Hotel,
    title: "Vuelo + Hotel",
    desc: "Combina tu vuelo con hospedaje. Nos dices tu presupuesto, categoría, tipo de habitación y zona; te armamos el paquete que más se ajuste a ti.",
  },
  {
    icon: BadgeDollarSign,
    title: "Precios que no encuentras solo",
    desc: "Accedemos a tarifas de mayorista y consolidadores. Aprovechas condiciones que no aparecen en los buscadores públicos.",
  },
  {
    icon: Globe2,
    title: "Cualquier destino",
    desc: "Desde una escapada regional hasta la vuelta al mundo. Cuéntanos a dónde quieres ir y nosotros trazamos la ruta.",
  },
];

const AereosLandingPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a14]" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }} data-testid="aereos-landing">
      {/* HEADER */}
      <header className="bg-[#0f1629] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <a href="/" data-testid="back-home" className="flex items-center gap-2 text-slate-300 hover:text-white font-medium text-sm transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Regresar
          </a>
          <div className="w-px h-5 bg-white/20" />
          <p className="font-bold text-white text-sm">Cotización de vuelos y paquetes aéreos</p>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})`, filter: "brightness(0.4)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a14] via-[#0a0a14]/50 to-transparent" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 pt-20 pb-16 text-center">
          <p className="inline-block bg-[#45A0EE]/15 text-[#45A0EE] text-[11px] uppercase tracking-[3px] px-4 py-1.5 rounded-full font-mono mb-5 border border-[#45A0EE]/30">
            Departamento de vuelos · Club Viajemos
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mb-5">
            Cotiza tus vuelos<br />
            <span className="text-[#45A0EE]">al mejor precio</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Tickets aéreos y paquetes Vuelo + Hotel a cualquier destino. Cuéntanos qué necesitas y nuestro equipo te consigue la mejor tarifa disponible.
          </p>
        </div>
      </section>

      {/* PROGRAMA */}
      <section className="px-4 py-16 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">¿Qué podemos cotizarte?</h2>
            <p className="text-slate-400 text-sm sm:text-base max-w-2xl mx-auto">
              Desde un boleto sencillo hasta un paquete completo con hospedaje. Tú eliges, nosotros conseguimos el mejor precio.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PROGRAM.map((p) => (
              <div key={p.title} className="bg-[#0f1629] border border-white/10 rounded-2xl p-6 hover:border-[#45A0EE]/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#45A0EE] to-[#1D6FB8] flex items-center justify-center flex-shrink-0">
                    <p.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-white font-black text-lg leading-none">{p.title}</h3>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ASESORIA / DEPTO ESPECIALIZADO */}
      <section className="px-4 pb-10">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#45A0EE]/10 to-transparent border border-[#45A0EE]/30 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#45A0EE] flex items-center justify-center flex-shrink-0">
              <Headset className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white">Tu solicitud pasa a nuestro departamento de cotización</h2>
          </div>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
            Cuando envías tu solicitud, esta llega directamente a nuestro <strong className="text-white">departamento especializado en cotización</strong>.
            Un asesor te contactará para confirmar los detalles —fechas, aerolínea, equipaje, hospedaje— y brindarte la mayor asesoría posible,
            para que tu viaje sea exactamente el que solicitaste.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: ShieldCheck, t: "Tarifas exclusivas", d: "Precios de mayorista y consolidador." },
              { icon: Clock, t: "Respuesta rápida", d: "Te contactamos con las opciones." },
              { icon: Headset, t: "Asesoría completa", d: "Ajustamos cada detalle a tu viaje." },
            ].map((b) => (
              <div key={b.t} className="bg-[#0f1629]/60 border border-white/10 rounded-xl p-4">
                <b.icon className="h-5 w-5 text-[#45A0EE] mb-2" />
                <p className="text-white font-bold text-sm">{b.t}</p>
                <p className="text-slate-400 text-xs mt-1">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COTIZADOR */}
      <section className="px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          <FlightQuoteForm />
        </div>
      </section>
    </div>
  );
};

export default AereosLandingPage;
